import { useState } from "react";
import ChatInput from "@/components/ChatInput";
import ChatMessage, { ChatMessage as ChatMessageType } from "@/components/ChatMessage";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";

const SUPABASE_URL = "https://gqyygdjrbygmdbrypwim.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdxeXlnZGpyYnlnbWRicnlwd2ltIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzg2MzM4OTcsImV4cCI6MjA1NDIwOTg5N30.DuqsJUu5Y6rZyB2toe9LOtelJg-zfBj7Sdh9Weh1aBM";

const Index = () => {
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsLoading(true);
    const uploadPromises = Array.from(files).map(async (file) => {
      try {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
          reader.onload = async (e) => {
            try {
              const content = e.target?.result as string;
              
              const response = await fetch(
                `${SUPABASE_URL}/functions/v1/process-document`,
                {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    content,
                    metadata: { filename: file.name }
                  }),
                }
              );

              if (!response.ok) throw new Error('Failed to process document');
              resolve(file.name);
            } catch (error) {
              reject(error);
            }
          };
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsText(file);
        });
      } catch (error) {
        return Promise.reject(error);
      }
    });

    try {
      const results = await Promise.allSettled(uploadPromises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (successful > 0) {
        toast({
          title: "Success",
          description: `Successfully processed ${successful} document${successful !== 1 ? 's' : ''}${failed > 0 ? ` (${failed} failed)` : ''}`,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to process documents",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (content: string) => {
    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: "user",
      content,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/chat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ message: content }),
        }
      );

      if (!response.ok) throw new Error('Failed to get response');
      
      const { response: aiResponse } = await response.json();
      
      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: aiResponse,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen max-h-screen">
      <header className="flex items-center justify-between p-4 bg-deepseeker-gray">
        <h1 className="text-xl font-mono font-bold text-deepseeker-blue">
          DeepSeeker Coder
        </h1>
        <div className="flex items-center gap-2">
          <input
            type="file"
            id="file-upload"
            className="hidden"
            onChange={handleFileUpload}
            accept=".txt,.js,.ts,.jsx,.tsx,.md"
            multiple
          />
          <label htmlFor="file-upload">
            <Button
              variant="outline"
              className="cursor-pointer"
              disabled={isLoading}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Documents
            </Button>
          </label>
        </div>
      </header>
      
      <main className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-deepseeker-text/60">
            <p className="text-center">
              Start a conversation with DeepSeeker Coder!
              <br />
              Ask me anything about coding or upload documents for context.
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))
        )}
      </main>

      <ChatInput onSend={handleSendMessage} disabled={isLoading} />
    </div>
  );
};

export default Index;